import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import DefaultLayout from '../components/layouts/DefaultLayout'
import {
  Box,
  Text,
  Container,
  Heading,
  VStack,
  HStack,
  Divider,
  Image,
  useBreakpointValue,
  Grid,
  GridItem,
} from '@chakra-ui/react'
import { Map, MapMarker } from 'react-kakao-maps-sdk'

const Introduction: React.FC = () => {
  const { t } = useTranslation()
  const isMobile = useBreakpointValue({ base: true, md: false })

  const officeLocations = [
    { name: '서울', address: '서울시 송파구 충민로 66 가든 파이브 라이프 테크노관 t-10051 (지하1층)' },
    { name: '광주/나주/목포', address: '나주시 혁신도시 빛가람동 196-3 예가람타워 212호' },
    { name: '세종', address: '세종특별자치시 보람동 711 금강베네치아 4층 403호' },
    { name: '대구', address: '대구시 남구 대명로92 김재근한의원 3층' },
    { name: '구미', address: '구미시 왕산로71 노블타운3층 301호' },
    { name: '청주1', address: '청주시 상당구 교서로8-4 3층 컴장부' },
    { name: '청주2', address: '청주시 흥덕구 풍산로47번길 37-3(1층)' },
    { name: '인천', address: '인천시 미추홀구 주안동 216-1 쿠노시아 일식집 3층' },
    { name: '안산', address: '경기도 안산시 단원구 고잔로 112 신광프라자 406호' },
    { name: '전주1', address: '전주시 완산구 하거마7길26 1층 빨강 (벌떼정육식당 뒷건물)' },
    { name: '전주2', address: '완산구 삼천천변2길 36-19 1층 세우리교회(명재어린이집 뒤쪽)' },
    { name: '부산', address: '부산광역시 부산진구 중앙대로 694, 9층' },
    { name: '익산', address: '전라북도 익산시 고봉로32길33 1층' }
  ]

  const [selectedOffice, setSelectedOffice] = useState(officeLocations[0])

  const historyData = [
    { year: '2024.11', content: '[토계] 브랜드 출범' },
    { year: '2023~2024', content: '전국 주요 프로젝트 낙찰 사례 축적' },
    { year: '2010~2020', content: '지역 단위 토지 개발, 도로 개설 경험 확대' },
    { year: '2000년대', content: '임야/맹지 → 도로 개설 통한 가치 상승 사례 다수' },
    { year: '1980년대', content: '부동산 경매 투자 노하우 축적 시작' },
  ]

  return (
    <DefaultLayout>
      <Container maxW="container.xl" py={10}>
        <VStack spacing={12} align="stretch">
          {/* 회장 인사말 */}
          <Box>
            <Box textAlign="center" mb={8}>
              <Heading
                as="h1"
                size={isMobile ? 'xl' : '2xl'}
                bgGradient="linear(to-r, brand.400, brand.600)"
                bgClip="text"
                fontWeight="bold"
                mb={4}
              >
                회장 인사말
              </Heading>
            </Box>

            <Box
              p={8}
              bg="white"
              borderRadius="lg"
              boxShadow="md"
              border="1px solid"
              borderColor="gray.200"
            >
              <VStack spacing={6} align="stretch">
                <VStack spacing={4} align="stretch">
                  <Text fontSize="xl" fontWeight="bold" color="brand.600" textAlign="center">
                    토계를 찾아주신 여러분, 진심으로 환영합니다.
                  </Text>

                  <Text
                    fontSize="md"
                    color="gray.700"
                    lineHeight="tall"
                    whiteSpace="pre-wrap"
                    textAlign="center"
                  >
                    {`토계는 "길 내는 사람들",
서로의 마음과 마음의 길을 여는 것을 시작으로 
길을 만들고 함께 성장하는 공동체를 목표로 합니다.

우리는 투명한 운영, 책임 있는 개발,
그리고 모두가 주인이 되는 구조를 중심 가치로 삼아
누구나 소액으로도 대한민국 땅을 함께 소유하고
함께 부를 이루는 새로운 길을 열고 있습니다.

토계의 길은 혼자가 아닌 함께 걷는 길입니다.
여러분의 참여와 신뢰가 토계를 앞으로 나아가게 하고,
사회적 나눔과 상생의 가치까지 확장시키고 있습니다.

"우리 땅은 우리가 지킨다"는 마음으로
토계는 앞으로도 대한민국의 건강한 부의 생태계를 만들고,
더 많은 이웃이 함께 서는 길을 준비하겠습니다.

토계와 함께 걸어주셔서 감사합니다.
여러분 모두가 길을 내는 사람들입니다.`}
                  </Text>

                  <Box mt={6} pt={6} borderTop="1px solid" borderColor="gray.200">
                    <HStack spacing={4} mt={4} justify="flex-end" align="flex-end">
                      <VStack align="flex-end" spacing={1}>
                        <Text fontSize="sm" fontWeight="bold" color="gray.700">
                          토계 회장 올림
                        </Text>
                      </VStack>
                      <Image
                        src="/images/sign.png"
                        alt="사인"
                        maxH="60px"
                        w="auto"
                        objectFit="contain"
                      />
                    </HStack>
                  </Box>
                </VStack>
              </VStack>
            </Box>
          </Box>

          {/* 회사 소개 */}
          <Box>
            <Box textAlign="center" mb={8}>
              <Heading
                as="h2"
                size={isMobile ? 'xl' : '2xl'}
                bgGradient="linear(to-r, brand.400, brand.600)"
                bgClip="text"
                fontWeight="bold"
                mb={4}
              >
                회사 소개
              </Heading>
              <Text fontSize="sm" color="gray.500" fontWeight="bold" letterSpacing="wide">
                About Toge
              </Text>
            </Box>

            <Box
              p={8}
              bg="white"
              borderRadius="lg"
              boxShadow="md"
              border="1px solid"
              borderColor="gray.200"
            >
              <VStack spacing={4} align="stretch">
                <Text fontSize="lg" fontWeight="bold" color="brand.600" textAlign="center">
                  함께 소유하고, 함께 성장하는 부동산 공동체 – 토계
                </Text>
                <Divider />
                <VStack spacing={4} align="stretch">
                  <Text fontSize="md" color="gray.700" lineHeight="tall">
                    토계는 부동산 경매의 높은 장벽을 낮추고,
                  </Text>
                  <Text fontSize="md" color="gray.700" lineHeight="tall">
                    누구나 소액으로도 우리 땅의 주인이 될 수 있는
                  </Text>
                  <Text fontSize="md" color="gray.700" lineHeight="tall">
                    사람과 사람의 마음의길을 내고
                  </Text>
                  <Text fontSize="md" color="gray.700" lineHeight="tall">
                    지역과 지역이 함께 성장하며,
                  </Text>
                  <Text fontSize="md" color="gray.700" lineHeight="tall">
                    대한민국의 소중한 땅을 지켜내는 공동체 브랜드입니다.
                  </Text>
                  <Box mt={4} pt={4} borderTop="1px solid" borderColor="gray.200">
                    <Text fontSize="md" color="gray.700" lineHeight="tall">
                      투명한 운영, 책임 있는 개발,
                    </Text>
                    <Text fontSize="md" color="gray.700" lineHeight="tall">
                      그리고 가족공동체의 힘을 기반으로
                    </Text>
                    <Text fontSize="md" color="brand.600" fontWeight="bold" lineHeight="tall">
                      미래형 부동산 커뮤니티로 비상하고 있습니다.
                    </Text>
                  </Box>
                </VStack>
              </VStack>
            </Box>
          </Box>

          {/* 투자 소개 */}
          <Box>
            <Box textAlign="center" mb={8}>
              <Heading
                as="h2"
                size={isMobile ? 'xl' : '2xl'}
                bgGradient="linear(to-r, brand.400, brand.600)"
                bgClip="text"
                fontWeight="bold"
                mb={4}
              >
                투자 소개
              </Heading>
              <Text fontSize="sm" color="gray.500" fontWeight="bold" letterSpacing="wide">
                Investment
              </Text>
            </Box>

            <Box
              p={8}
              bg="white"
              borderRadius="lg"
              boxShadow="md"
              border="1px solid"
              borderColor="gray.200"
            >
              <VStack spacing={6} align="stretch">
                <Text fontSize="lg" fontWeight="bold" color="brand.600" textAlign="center">
                  소액으로 시작해 실물 자산의 가치를
                </Text>
                <Text fontSize="md" color="gray.700" textAlign="center" fontWeight="bold">
                  토계가 새로운 표준으로 제시합니다.
                </Text>
                <Divider />
                <VStack spacing={3} align="stretch">
                  <HStack spacing={2}>
                    <Text fontSize="lg" color="brand.600">✔</Text>
                    <Text fontSize="md" color="gray.700">부동산 경매</Text>
                  </HStack>
                  <HStack spacing={2}>
                    <Text fontSize="lg" color="brand.600">✔</Text>
                    <Text fontSize="md" color="gray.700">개발·운영 프로젝트</Text>
                  </HStack>
                  <HStack spacing={2}>
                    <Text fontSize="lg" color="brand.600">✔</Text>
                    <Text fontSize="md" color="gray.700">공유지분 투자 모델</Text>
                  </HStack>
                </VStack>
                <Box mt={4} p={4} bg="gray.50" borderRadius="md">
                  <Text fontSize="md" color="gray.700" fontWeight="bold" mb={2}>
                    누구나 참여할 수 있는 실물 기반 안정형 투자 시스템
                  </Text>
                </Box>
                <Divider />
                <VStack spacing={4} align="stretch">
                  <Text fontSize="md" color="gray.700" lineHeight="tall">
                    투자 구조는 단순하지만 강력합니다.
                  </Text>
                  <Text fontSize="md" color="gray.700" lineHeight="tall">
                    실제 부동산을 기반으로 하기 때문에
                  </Text>
                  <Text fontSize="md" color="gray.700" lineHeight="tall">
                    수익과 안전성, 투명성이 균형을 이루며
                  </Text>
                  <Text fontSize="md" color="gray.700" lineHeight="tall">
                    투자자는 프로젝트의 일부가 아닌
                  </Text>
                  <Text fontSize="md" color="brand.600" fontWeight="bold" lineHeight="tall" fontStyle="italic">
                    "함께 만드는 주인공"이 됩니다.
                  </Text>
                  <Box mt={4} pt={4} borderTop="1px solid" borderColor="gray.200">
                    <Text fontSize="md" color="gray.700" lineHeight="tall">
                      투자자의 자산을 지키고
                    </Text>
                    <Text fontSize="md" color="gray.700" lineHeight="tall">
                      함께 나누는 수익을 만들어가는
                    </Text>
                    <Text fontSize="md" color="brand.600" fontWeight="bold" lineHeight="tall">
                      건강한 투자 생태계를 지향합니다.
                    </Text>
                  </Box>
                </VStack>
              </VStack>
            </Box>
          </Box>

          {/* 연혁 타임라인 */}
          <Box>
            <Box textAlign="center" mb={8}>
              <Heading
                as="h2"
                size={isMobile ? 'xl' : '2xl'}
                bgGradient="linear(to-r, brand.400, brand.600)"
                bgClip="text"
                fontWeight="bold"
                mb={4}
              >
                연혁 타임라인
              </Heading>
            </Box>

            <Box
              p={8}
              bg="white"
              borderRadius="lg"
              boxShadow="md"
              border="1px solid"
              borderColor="gray.200"
            >
              <VStack spacing={0} align="stretch">
                {historyData.map((item, index) => (
                  <React.Fragment key={index}>
                    <HStack
                      spacing={6}
                      align="flex-start"
                      py={4}
                      _hover={{ bg: 'gray.50' }}
                      px={4}
                      borderRadius="md"
                    >
                      <Box
                        minW="120px"
                        textAlign={isMobile ? 'left' : 'right'}
                        fontWeight="bold"
                        color="brand.600"
                        fontSize={isMobile ? 'sm' : 'md'}
                      >
                        {item.year}
                      </Box>
                      <Box
                        flex="1"
                        pl={isMobile ? 0 : 6}
                        borderLeft={isMobile ? 'none' : '3px solid'}
                        borderColor="brand.300"
                        position="relative"
                      >
                        {!isMobile && (
                          <Box
                            position="absolute"
                            left="-8px"
                            top="8px"
                            w="12px"
                            h="12px"
                            borderRadius="full"
                            bg="brand.500"
                            border="3px solid white"
                            boxShadow="0 0 0 2px brand.500"
                          />
                        )}
                        <Text fontSize={isMobile ? 'sm' : 'md'} color="gray.700">
                          {item.content}
                        </Text>
                      </Box>
                    </HStack>
                    {index < historyData.length - 1 && (
                      <Divider borderColor="gray.200" />
                    )}
                  </React.Fragment>
                ))}
              </VStack>
            </Box>
          </Box>

          {/* 비전 */}
          <Box>
            <Box textAlign="center" mb={8}>
              <Heading
                as="h2"
                size={isMobile ? 'xl' : '2xl'}
                bgGradient="linear(to-r, brand.400, brand.600)"
                bgClip="text"
                fontWeight="bold"
                mb={4}
              >
                비전
              </Heading>
              <Text fontSize="sm" color="gray.500" fontWeight="bold" letterSpacing="wide">
                Vision
              </Text>
            </Box>

            <Box
              p={8}
              bg="white"
              borderRadius="lg"
              boxShadow="md"
              border="1px solid"
              borderColor="gray.200"
            >
              <VStack spacing={6} align="stretch">
                <Text fontSize="xl" fontWeight="bold" color="brand.600" textAlign="center">
                  내나라, 내 땅은 우리가 지킨다,
                </Text>
                <Text fontSize="lg" color="gray.700" textAlign="center" fontWeight="bold">
                  대한민국 부동산 공동체의 새로운 모델
                </Text>
                <Divider />
                <VStack spacing={4} align="stretch">
                  <Text fontSize="md" color="gray.700" lineHeight="tall">
                    우리는 대한민국 땅을 공동의 자산으로 바라보며,
                  </Text>
                  <Text fontSize="md" color="gray.700" lineHeight="tall">
                    모두가 함께 참여하고 함께 지켜내는
                  </Text>
                  <Text fontSize="md" color="brand.600" fontWeight="bold" lineHeight="tall">
                    지속 가능한 부동산 공동체를 만들어 갑니다.
                  </Text>
                </VStack>
                <Divider />
                <VStack spacing={3} align="stretch">
                  <Text fontSize="md" color="gray.700" lineHeight="tall">
                    • 대한민국 경매 시장의 대중화
                  </Text>
                  <Text fontSize="md" color="gray.700" lineHeight="tall">
                    • 누구나 참여 가능한 소액 부동산 구조
                  </Text>
                  <Text fontSize="md" color="gray.700" lineHeight="tall">
                    • 투명하고 합법적인 공동투자 시스템
                  </Text>
                  <Text fontSize="md" color="gray.700" lineHeight="tall">
                    • 지역경제와 사람을 연결하는 상생 모델
                  </Text>
                  <Text fontSize="md" color="gray.700" lineHeight="tall">
                    • 공동체의 힘으로 땅을 지키고 가치 상승을 함께 누리는 구조
                  </Text>
                  <Text fontSize="md" color="gray.700" lineHeight="tall">
                    • 사회적 약자를 보호하는 기금을 마련 후원하고 미래 세대 교육 지원을 통해 인재를 양성 배출
                  </Text>
                </VStack>
                <Box mt={4} p={4} bg="brand.50" borderRadius="md" borderLeft="4px solid" borderColor="brand.500">
                  <Text fontSize="md" color="gray.700" lineHeight="tall" fontStyle="italic" textAlign="center">
                    "혼자는 땅을 살 수 없지만,
                  </Text>
                  <Text fontSize="md" color="brand.600" fontWeight="bold" lineHeight="tall" fontStyle="italic" textAlign="center">
                    함께라면 대한민국을 지킬 수 있습니다."
                  </Text>
                </Box>
              </VStack>
            </Box>
          </Box>

          {/* 브랜드 철학 */}
          <Box>
            <Box textAlign="center" mb={8}>
              <Heading
                as="h2"
                size={isMobile ? 'xl' : '2xl'}
                bgGradient="linear(to-r, brand.400, brand.600)"
                bgClip="text"
                fontWeight="bold"
                mb={4}
              >
                브랜드 철학
              </Heading>
              <Text fontSize="sm" color="gray.500" fontWeight="bold" letterSpacing="wide">
                Philosophy
              </Text>
            </Box>

            <Box
              p={8}
              bg="white"
              borderRadius="lg"
              boxShadow="md"
              border="1px solid"
              borderColor="gray.200"
            >
              <VStack spacing={4} align="stretch">
                <Text fontSize="lg" color="gray.700" lineHeight="tall" textAlign="center">
                  사람과 땅을 연결하고,
                </Text>
                <Text fontSize="lg" color="gray.700" lineHeight="tall" textAlign="center">
                  가치를 키워 모두가 함께 성장하는 길.
                </Text>
              </VStack>
            </Box>
          </Box>

          {/* 핵심가치 */}
          <Box>
            <Box textAlign="center" mb={8}>
              <Heading
                as="h2"
                size={isMobile ? 'xl' : '2xl'}
                bgGradient="linear(to-r, brand.400, brand.600)"
                bgClip="text"
                fontWeight="bold"
                mb={4}
              >
                핵심 가치
              </Heading>
              <Text fontSize="sm" color="gray.500" fontWeight="bold" letterSpacing="wide">
                Core Value
              </Text>
            </Box>

            <Grid
              templateColumns={isMobile ? '1fr' : 'repeat(2, 1fr)'}
              gap={6}
            >
              <GridItem>
                <Box
                  p={6}
                  bg="white"
                  borderRadius="lg"
                  boxShadow="md"
                  border="1px solid"
                  borderColor="gray.200"
                  h="100%"
                >
                  <VStack spacing={4} align="stretch">
                    <Heading size="md" color="brand.600">
                      1. 투명한 운영과 책임 있는 개발
                    </Heading>
                    <Divider />
                    <Text fontSize="sm" color="gray.700" lineHeight="tall">
                      모두가 신뢰할 수 있는 구조로, 참여자 모두가 주인이 되는 시스템을 지향합니다.
                    </Text>
                  </VStack>
                </Box>
              </GridItem>

              <GridItem>
                <Box
                  p={6}
                  bg="white"
                  borderRadius="lg"
                  boxShadow="md"
                  border="1px solid"
                  borderColor="gray.200"
                  h="100%"
                >
                  <VStack spacing={4} align="stretch">
                    <Heading size="md" color="brand.600">
                      2. 함께 이루는 상생 공동체
                    </Heading>
                    <Divider />
                    <Text fontSize="sm" color="gray.700" lineHeight="tall">
                      함께 모아, 함께 이루고, 함께 나누는 협력의 힘으로 공동 번영을 추구합니다.
                    </Text>
                  </VStack>
                </Box>
              </GridItem>

              <GridItem>
                <Box
                  p={6}
                  bg="white"
                  borderRadius="lg"
                  boxShadow="md"
                  border="1px solid"
                  borderColor="gray.200"
                  h="100%"
                >
                  <VStack spacing={4} align="stretch">
                    <Heading size="md" color="brand.600">
                      3. 나눔으로 완성되는 진정한 부(富)
                    </Heading>
                    <Divider />
                    <Text fontSize="sm" color="gray.700" lineHeight="tall">
                      성공을 혼자만의 것이 아닌, 이웃과 사회와 나누는 가치로 확장합니다.
                    </Text>
                  </VStack>
                </Box>
              </GridItem>

              <GridItem>
                <Box
                  p={6}
                  bg="white"
                  borderRadius="lg"
                  boxShadow="md"
                  border="1px solid"
                  borderColor="gray.200"
                  h="100%"
                >
                  <VStack spacing={4} align="stretch">
                    <Heading size="md" color="brand.600">
                      4. 사회적 약자와의 동행
                    </Heading>
                    <Divider />
                    <Text fontSize="sm" color="gray.700" lineHeight="tall">
                      도움이 필요한 이들을 위한 지원과 희망 프로그램을 지속적으로 실천합니다.
                    </Text>
                  </VStack>
                </Box>
              </GridItem>
            </Grid>
          </Box>

          {/* 오시는 길 */}
          <Box>
            <Box textAlign="center" mb={8}>
              <Heading
                as="h2"
                size={isMobile ? 'xl' : '2xl'}
                bgGradient="linear(to-r, brand.400, brand.600)"
                bgClip="text"
                fontWeight="bold"
                mb={4}
              >
                오시는 길
              </Heading>
            </Box>

            <Box
              p={8}
              bg="white"
              borderRadius="lg"
              boxShadow="md"
              border="1px solid"
              borderColor="gray.200"
            >
              <VStack spacing={6} align="stretch">
                <HStack
                  spacing={6}
                  align={{ base: 'stretch', md: 'flex-start' }}
                  flexDir={{ base: 'column', md: 'row' }}
                >
                  <Box
                    w={{ base: '100%', md: '35%' }}
                    borderRight={{ base: 'none', md: '1px solid' }}
                    borderColor="gray.200"
                    pr={{ base: 0, md: 4 }}
                  >
                    <Text fontSize="lg" fontWeight="bold" color="brand.600" mb={4}>
                      지점 선택
                    </Text>
                    <VStack align="stretch" spacing={3} maxH="400px" overflowY="auto">
                      {officeLocations.map((office) => {
                        const isActive = office.name === selectedOffice.name
                        return (
                          <Box
                            key={office.name}
                            p={4}
                            borderRadius="md"
                            border="1px solid"
                            borderColor={isActive ? 'brand.500' : 'gray.200'}
                            bg={isActive ? 'brand.50' : 'white'}
                            cursor="pointer"
                            onClick={() => setSelectedOffice(office)}
                            transition="all 0.2s"
                            _hover={{
                              borderColor: 'brand.400',
                              boxShadow: 'sm'
                            }}
                          >
                            <Text fontWeight="bold" color="gray.800">
                              ● {office.name}
                            </Text>
                            <Text fontSize="sm" color="gray.600" mt={1} whiteSpace="pre-wrap">
                              {office.address}
                            </Text>
                          </Box>
                        )
                      })}
                    </VStack>
                  </Box>
                  <Box w={{ base: '100%', md: '65%' }}>
                    <Text fontSize="lg" fontWeight="bold" color="brand.600" mb={2}>
                      {selectedOffice.name}
                    </Text>
                    <Text fontSize="md" color="gray.700" mb={4}>
                      {selectedOffice.address}
                    </Text>
                    <Divider mb={4} />
                    <KakaoMap address={selectedOffice.address} title={selectedOffice.name} />
                  </Box>
                </HStack>
              </VStack>
            </Box>
          </Box>
        </VStack>
      </Container>
    </DefaultLayout>
  )
}

// 카카오맵 컴포넌트
interface KakaoMapProps {
  address: string
  title: string
}

const KakaoMap: React.FC<KakaoMapProps> = ({ address, title }) => {
  const [position, setPosition] = useState<{ lat: number; lng: number }>({
    lat: 37.477128,
    lng: 127.112538,
  })
  const [isOpen, setIsOpen] = useState(true)

  // 주소를 좌표로 변환하는 함수
  useEffect(() => {
    if (window.kakao && window.kakao.maps && window.kakao.maps.services) {
      const geocoder = new window.kakao.maps.services.Geocoder()
      geocoder.addressSearch(address, (result: any, status: any) => {
        if (status === window.kakao.maps.services.Status.OK && result && result[0]) {
          setPosition({
            lat: parseFloat(result[0].y),
            lng: parseFloat(result[0].x),
          })
        }
      })
    }
  }, [address])

  return (
    <Box
      w="100%"
      h={{ base: '300px', md: '400px' }}
      minH={{ base: '300px', md: '400px' }}
      borderRadius="lg"
      overflow="hidden"
      border="1px solid"
      borderColor="gray.200"
      position="relative"
    >
      <Map
        center={position}
        style={{ width: '100%', height: '100%' }}
        level={3}
      >
        <MapMarker position={position} onClick={() => setIsOpen(!isOpen)}>
          {isOpen && (
            <div style={{ padding: '5px', fontSize: '12px', textAlign: 'center', minWidth: '150px' }}>
              <strong>{title}</strong>
              <br />
              {address}
            </div>
          )}
        </MapMarker>
      </Map>
    </Box>
  )
}

// 카카오맵 타입 선언
declare global {
  interface Window {
    kakao: any
  }
}

export default Introduction

